import { extractName, extractOrdinalValues } from "./extract";

const handleWhatIfPayload = (conditionGroups, analysisId) => {
  const column_adjustments = conditionGroups.map(
    ({ columnName, columnValue, change }) => ({
      column_name: columnName.value,
      event_adjustments: [
        {
          id: columnValue.value.id,
          probability: {
            "@type": "categorical",
            value: change / 100,
          },
        },
      ],
    })
  );

  const payload = {
    job_id: analysisId,
    settings: {
      adjustment: {
        column_adjustments,
      },
      model_id: null,
    },
  };

  return payload;
};

const getConditions = variables => {
  const renderList = variables.reduce((accumulator, variable) => {
    const { column_name: columnName } = variable;
    let processed;
    if (variable["@type"] === "numeric") {
      const { intervals, isolated_points } = variable;
      const processedIntervals = intervals.map(val => {
        const { interval } = val ?? {};
        const label = extractName(interval, undefined);
        const { event_probability } = val;
        return {
          label,
          value: { id: val.id, limit: event_probability * 100 },
        };
      });
      const processedIsolatedPoints = isolated_points?.map(val => {
        const { id, value, event_probability } = val;
        return {
          label: value,
          value: { id, limit: event_probability * 100 },
        };
      });

      processed = {
        [columnName]: {
          columnValues: [
            ...processedIntervals,
            ...(processedIsolatedPoints && processedIsolatedPoints),
          ],
        },
      };
      return { ...accumulator, ...processed };
    } else if (variable["@type"] === "ordinal") {
      const { intervals, isolated_points, values } = variable;
      const processedIntervals = intervals.map(val => {
        const { id } = val ?? {};
        const label = extractOrdinalValues(variable, id);
        const { event_probability } = val;
        return {
          label,
          value: { id: val.id, limit: event_probability * 100 },
        };
      });
      const processedIsolatedPoints = isolated_points?.map(val => {
        const { id, value, event_probability } = val;
        const actualValue = values.find(
          actualValue => actualValue.id === value
        );
        return {
          label: actualValue?.name ?? "blank value",
          value: { id, limit: event_probability * 100 },
        };
      });
      processed = {
        [columnName]: {
          columnValues: [
            ...processedIntervals,
            ...(processedIsolatedPoints && processedIsolatedPoints),
          ],
        },
      };
      return { ...accumulator, ...processed };
    } else if (variable["@type"] === "categorical") {
      const { values } = variable;
      const processedValues = values.map(val => {
        const { id, name, event_probability } = val;
        return {
          label: name ?? "blank value",
          value: { id, limit: event_probability * 100 },
        };
      });
      processed = { [columnName]: { columnValues: processedValues } };
      return { ...accumulator, ...processed };
    }

    return accumulator;
  }, {});
  return renderList;
};

const getAdjustments = (adjustments, variableData) =>
  adjustments?.map(({ column_name, event_adjustments }) => {
    const { final_probability, initial_probability, id } = event_adjustments[0];
    const column = variableData.find(
      variable => variable.column_name === column_name
    );
    const { "@type": type } = column ?? {};
    let value = "";
    if (type === "ordinal") {
      value = extractOrdinalValues(column, id);
    }

    if (type === "numeric") {
      const { intervals, isolated_points } = column;
      const foundInterval = intervals.find(interval => interval.id === id);
      const isolatedPoint = isolated_points?.find(point => point.id === id);
      value = extractName(foundInterval?.interval, isolatedPoint);
    }

    if (type === "categorical") {
      const { values } = column;
      const toProcess = values.find(value => value.id === id);
      value = toProcess?.name ?? "blank value";
    }
    const change = ((final_probability - initial_probability) * 100).toFixed(2);

    return {
      columnName: column_name,
      value,
      change,
    };
  });

export { handleWhatIfPayload, getConditions, getAdjustments };
